import React, { useEffect, useState } from "react";
import { shareApi } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus } from "lucide-react";

// Each investor gets one personal room link. The key is shown once, right after it is minted —
// the server only keeps a hash, so "New link" is the way to hand someone a fresh one.
export const RoomInvites = ({ shareId }) => {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setRows((await shareApi.inviteList(shareId)).invites || []); } catch { /* not published yet */ }
  };
  useEffect(() => { if (shareId) load(); }, [shareId]); // eslint-disable-line react-hooks/exhaustive-deps

  const issue = async (r) => {
    const to = r || { name, email };
    if (!to.email.trim()) return toast.error("Enter the investor's email");
    setBusy(true);
    try {
      const res = await shareApi.invites(shareId, [{ name: to.name || "", email: to.email.trim() }]);
      const inv = (res.invites || [])[0];
      if (!inv) throw new Error("no invite");
      setFresh(inv);
      navigator.clipboard?.writeText(inv.link).catch(() => {});
      toast.success(`Personal link for ${inv.email} copied`);
      if (!r) { setName(""); setEmail(""); }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the link"); }
    finally { setBusy(false); }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke ${row.email}'s access? Their link stops working immediately.`)) return;
    try {
      await shareApi.revokeInvite(shareId, row.id);
      setRows((rs) => rs.filter((x) => x.id !== row.id));
      toast.success("Access revoked");
    } catch { toast.error("Could not revoke"); }
  };

  return (
    <div className="ri-wrap" data-testid="room-invites">
      <div className="ri-add">
        <input className="inp" placeholder="Investor name" value={name} onChange={(e) => setName(e.target.value)} data-testid="ri-name" />
        <input className="inp" placeholder="name@fund.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ri-email" />
        <button className="btn btn-primary" onClick={() => issue(null)} disabled={busy} data-testid="ri-create">
          <UserPlus size={12} style={{ marginRight: 5 }} />{busy ? "Creating…" : "Create link"}
        </button>
      </div>
      {fresh && (
        <div className="ri-fresh" data-testid="ri-fresh">
          <span className="mono">{fresh.link}</span>
          <button className="icon-btn" title="Copy link" onClick={() => { navigator.clipboard?.writeText(fresh.link).catch(() => {}); toast.success("Link copied"); }} data-testid="ri-copy-fresh"><Copy size={13} /></button>
          <div className="ra-sub">Copied — send it to {fresh.email}. This is the only time the link is shown; use "New link" to issue another.</div>
        </div>
      )}
      {!rows.length && <div className="ra-sub" data-testid="ri-empty">Optional: a personal link opens the room without the passcode and tags every view, question and commitment to that investor.</div>}
      {rows.map((r) => (
        <div className="ri-row" key={r.id} data-testid={`ri-row-${r.id}`}>
          <div className="ri-who">
            <b>{r.name || r.email}</b>
            <span className="mono">{r.email}</span>
          </div>
          <span className="ri-opens" data-testid={`ri-opens-${r.id}`}>{r.lastViewedAt ? `${r.views || 0} open${r.views === 1 ? "" : "s"} · last ${new Date(r.lastViewedAt).toLocaleDateString()}` : "not opened yet"}</span>
          <button className="btn btn-sm" onClick={() => issue({ name: r.name, email: r.email })} disabled={busy} data-testid={`ri-reissue-${r.id}`}>New link</button>
          <button className="icon-btn" title="Revoke access" onClick={() => revoke(r)} data-testid={`ri-revoke-${r.id}`}><Trash2 size={13} /></button>
        </div>
      ))}
    </div>
  );
};
